export const FGTN_SITE_ORIGIN = 'https://anglerbuilt.com';

export function getFgtnEpisodePath(episodeNumber) {
  return `/fgtn/${episodeNumber}`;
}

export function getFgtnQrPath(episodeNumber) {
  return `/fgtn/${episodeNumber}/qr`;
}

export function getFgtnEpisodeUrl(episodeNumber) {
  return `${FGTN_SITE_ORIGIN}${getFgtnEpisodePath(episodeNumber)}`;
}

export function getFgtnQrUrl(episodeNumber) {
  return `${FGTN_SITE_ORIGIN}${getFgtnQrPath(episodeNumber)}`;
}

export function formatAirDate(isoDate) {
  if (!isoDate) return '';
  const [year, month, day] = String(isoDate).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function todayIsoDate() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function parseEpisodeNumber(value) {
  const n = Number.parseInt(String(value), 10);
  if (!Number.isInteger(n) || n < 1) return null;
  return n;
}

const ALLOWED_TAGS = new Set([
  'A', 'P', 'BR', 'UL', 'OL', 'LI', 'STRONG', 'EM', 'B', 'I',
  'H2', 'H3', 'H4', 'BLOCKQUOTE',
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH',
]);

const UNWRAP_TAGS = new Set([
  'SPAN', 'FONT', 'O:P', 'WBR', 'CENTER', 'SECTION', 'ARTICLE',
  'HTML', 'BODY', 'HEAD',
]);

const BLOCK_BREAK_TAGS = new Set([
  'P', 'DIV', 'H2', 'H3', 'H4', 'UL', 'OL', 'LI', 'TABLE', 'BLOCKQUOTE', 'BR',
]);
const LINE_CONTAINER_TAGS = new Set(['DIV', 'P', 'H1', 'H2', 'H3', 'H4']);

const URL_RE = /\bhttps?:\/\/[^\s<>"'）】\]]+/gi;

export function isSafeHref(href) {
  if (!href || typeof href !== 'string') return false;
  const trimmed = href.trim();
  try {
    const url = new URL(trimmed, FGTN_SITE_ORIGIN);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function appendCleaned(parent, cleaned) {
  if (!cleaned) return;
  parent.appendChild(cleaned);
}

function lastMeaningfulNode(frag) {
  let node = frag.lastChild;
  while (
    node &&
    node.nodeType === Node.TEXT_NODE &&
    !String(node.nodeValue || '').replace(/\u00a0/g, ' ').trim()
  ) {
    node = node.previousSibling;
  }
  return node;
}

function shouldAppendLineBreak(frag) {
  const node = lastMeaningfulNode(frag);
  if (!node) return true;
  return !BLOCK_BREAK_TAGS.has(node.nodeName);
}

function hasMeaningfulContent(el) {
  return Array.from(el.childNodes).some((node) => {
    if (node.nodeName === 'BR') return false;
    if (node.nodeType === Node.TEXT_NODE) {
      return String(node.nodeValue || '').replace(/\u00a0/g, ' ').trim().length > 0;
    }
    return true;
  });
}

function stripTrailingBreaks(root) {
  root.querySelectorAll('p, h2, h3, h4, li, td, th').forEach((el) => {
    while (el.lastChild && hasMeaningfulContent(el)) {
      const last = el.lastChild;
      if (last.nodeName === 'BR') {
        el.removeChild(last);
        continue;
      }
      if (
        last.nodeType === Node.TEXT_NODE &&
        !String(last.nodeValue || '').replace(/\u00a0/g, ' ').trim()
      ) {
        el.removeChild(last);
        continue;
      }
      break;
    }
  });
}

function countBreaks(html) {
  return (String(html).match(/<br\b/gi) || []).length;
}

function looksLikeDoubledOneNoteBreaks(html) {
  const pieces = String(html).split(/((?:<br\s*\/?>\s*)+)/i);
  const runs = [];
  for (let i = 1; i < pieces.length; i += 2) {
    const count = countBreaks(pieces[i]);
    if (count) runs.push(count);
  }
  if (runs.length < 2) return false;
  const singles = runs.filter((n) => n === 1).length;
  const doubles = runs.filter((n) => n === 2).length;
  return doubles > singles && doubles >= Math.ceil(runs.length / 2);
}

function restoreOneNoteBlankLines(html) {
  if (!looksLikeDoubledOneNoteBreaks(html)) return html;
  const pieces = String(html).split(/((?:<br\s*\/?>\s*)+)/i);
  return pieces
    .map((part, index) => {
      if (index % 2 === 0) return part;
      const next = Math.max(1, Math.round(countBreaks(part) / 2));
      return '<br>'.repeat(next);
    })
    .join('');
}

function cleanNode(node) {
  if (node.nodeType === Node.COMMENT_NODE) return null;

  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.nodeValue || '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const tag = node.tagName;
  if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'META' || tag === 'LINK' || tag === 'IFRAME') {
    return null;
  }

  if (tag === 'H1') {
    const el = document.createElement('h2');
    node.childNodes.forEach((child) => appendCleaned(el, cleanNode(child)));
    return el;
  }

  const shouldUnwrap = UNWRAP_TAGS.has(tag) || !ALLOWED_TAGS.has(tag);
  if (shouldUnwrap) {
    const frag = document.createDocumentFragment();
    node.childNodes.forEach((child) => appendCleaned(frag, cleanNode(child)));
    if (LINE_CONTAINER_TAGS.has(tag) && shouldAppendLineBreak(frag)) {
      frag.appendChild(document.createElement('br'));
    }
    return frag;
  }

  if (tag === 'A') {
    const href = node.getAttribute('href');
    if (!isSafeHref(href)) {
      const frag = document.createDocumentFragment();
      node.childNodes.forEach((child) => appendCleaned(frag, cleanNode(child)));
      return frag;
    }
    const el = document.createElement('a');
    el.setAttribute('href', href.trim());
    el.setAttribute('target', '_self');
    el.setAttribute('rel', 'noopener noreferrer');
    const title = node.getAttribute('title');
    if (title) el.setAttribute('title', title);
    node.childNodes.forEach((child) => appendCleaned(el, cleanNode(child)));
    return el;
  }

  const el = document.createElement(tag.toLowerCase());
  if (tag === 'TD' || tag === 'TH') {
    const colspan = node.getAttribute('colspan');
    if (colspan && /^\d+$/.test(colspan)) el.setAttribute('colspan', colspan);
  }
  node.childNodes.forEach((child) => appendCleaned(el, cleanNode(child)));
  return el;
}

function trimTrailingPunctuation(url) {
  return url.replace(/[.,;:!?)]+$/g, '');
}

function linkifyTextNodes(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let current = walker.nextNode();
  while (current) {
    textNodes.push(current);
    current = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    const parent = textNode.parentElement;
    if (!parent || parent.closest('a')) return;
    const value = textNode.nodeValue || '';
    if (!URL_RE.test(value)) {
      URL_RE.lastIndex = 0;
      return;
    }
    URL_RE.lastIndex = 0;

    const frag = document.createDocumentFragment();
    let lastIndex = 0;
    value.replace(URL_RE, (rawUrl, offset) => {
      const url = trimTrailingPunctuation(rawUrl);
      if (offset > lastIndex) {
        frag.appendChild(document.createTextNode(value.slice(lastIndex, offset)));
      }
      if (isSafeHref(url)) {
        const a = document.createElement('a');
        a.setAttribute('href', url);
        a.setAttribute('target', '_self');
        a.setAttribute('rel', 'noopener noreferrer');
        a.textContent = url;
        frag.appendChild(a);
        if (rawUrl.length > url.length) {
          frag.appendChild(document.createTextNode(rawUrl.slice(url.length)));
        }
      } else {
        frag.appendChild(document.createTextNode(rawUrl));
      }
      lastIndex = offset + rawUrl.length;
      return rawUrl;
    });
    if (lastIndex < value.length) {
      frag.appendChild(document.createTextNode(value.slice(lastIndex)));
    }
    parent.replaceChild(frag, textNode);
  });
}

export function linkifyPlainText(text) {
  const escaped = escapeHtml(text || '');
  const withBreaks = escaped.replace(/\r\n|\n|\r/g, '<br>');
  return sanitizeFgtnHtml(withBreaks);
}

function isLineEmpty(nodes) {
  return nodes.every((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return !String(node.nodeValue || '').replace(/\u00a0/g, ' ').trim();
    }
    return false;
  });
}

function wrapHardLines(root) {
  const blocks = new Set(['P', 'H2', 'H3', 'H4', 'UL', 'OL', 'TABLE', 'BLOCKQUOTE']);
  const children = Array.from(root.childNodes);
  const out = document.createDocumentFragment();
  let run = [];

  const flushRun = () => {
    if (!run.length) return;
    const lines = [[]];
    run.forEach((node) => {
      if (node.nodeName === 'BR') lines.push([]);
      else lines[lines.length - 1].push(node);
    });
    if (lines.length > 1 && isLineEmpty(lines[lines.length - 1])) {
      lines.pop();
    }
    lines.forEach((lineNodes) => {
      const div = document.createElement('div');
      if (isLineEmpty(lineNodes)) {
        div.className = 'fgtn-gap';
      } else {
        div.className = 'fgtn-line';
        lineNodes.forEach((node) => div.appendChild(node));
      }
      out.appendChild(div);
    });
    run = [];
  };

  children.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && blocks.has(node.nodeName)) {
      flushRun();
      out.appendChild(node);
      return;
    }
    run.push(node);
  });
  flushRun();
  root.replaceChildren(out);
}

export function sanitizeFgtnHtml(dirty) {
  if (!dirty || typeof dirty !== 'string') return '';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(dirty, 'text/html');
  const cleanRoot = document.createElement('div');
  Array.from(doc.body.childNodes).forEach((node) => appendCleaned(cleanRoot, cleanNode(node)));
  stripTrailingBreaks(cleanRoot);
  linkifyTextNodes(cleanRoot);

  const normalized = restoreOneNoteBlankLines(cleanRoot.innerHTML)
    .replace(/^(<br\s*\/?>)+|(<br\s*\/?>)+$/gi, '')
    .trim();
  const wrapRoot = document.createElement('div');
  wrapRoot.innerHTML = normalized;
  wrapHardLines(wrapRoot);
  return wrapRoot.innerHTML.trim();
}

export function isProbablyMissingTableError(error) {
  const message = (error && (error.message || error.details || '')) || '';
  const code = error && error.code;
  return code === '42P01' || /does not exist|schema cache|could not find the table/i.test(message);
}
